import { LitElement, html, nothing } from 'lit';
import { customElement, property, query, state } from 'lit/decorators.js';
import { map } from 'lit/directives/map.js';
import { Mission, SurveyForm, FormField, FieldType, FormValue, UserSurveyRequest, missionsApi } from 'lxl-data/missions';
import surveyModalStyles from './lxl-component-survey-modal.scss';
import { store } from 'lxl-data';

interface SurveyFormState {
  [questionId: string]: {
    answers: string[];
    openAnswer?: string;
  };
}

@customElement('lxl-component-survey-modal')
export class LxlComponentSurveyModal extends LitElement {
static styles = surveyModalStyles;

  @property({ type: Object })
  mission: Mission;

  @property()
  locale: string;

  @property()
  jwt: string;

  @property()
  onSurveyCompleted: () => void;

  @property({ type: Function })
  localize;

  @state()
  survey: SurveyForm | null = null;

  @state()
  currentQuestionIndex = 0;

  @state()
  formState: SurveyFormState = {};

  @state()
  isLoading = false;

  @state()
  isSubmitting = false;

  @state()
  showThankYou = false;

  @state()
  error: string | null = null;

  @query('lxl-component-modal')
  modalElement: LitElement & { close: () => {} };

  async connectedCallback() {
    super.connectedCallback();
    await this.loadSurvey();
  }

  async loadSurvey() {
    if (!this.mission?.surveyCode) return;

    this.isLoading = true;
    this.error = null;

    try {
      const result = await store.dispatch(
        missionsApi.endpoints.getSurvey.initiate({
          jwt: this.jwt,
          locale: this.locale,
          missionCode: this.mission.missionCode
        })
      ).unwrap();

      this.survey = result;
      this.initializeFormState();
    } finally {
      this.isLoading = false;
    }
  }

  initializeFormState() {
    if (!this.survey) return;

    const initialState: SurveyFormState = {};
    this.survey.questions.forEach(question => {
      initialState[question.id] = {
        answers: [],
        openAnswer: ''
      };
    });
    this.formState = initialState;
  }

  getCurrentQuestion(): FormField | null {
    if (!this.survey || this.currentQuestionIndex >= this.survey.questions.length) {
      return null;
    }
    return this.survey.questions[this.currentQuestionIndex];
  }

  isCurrentQuestionValid(): boolean {
  const currentQuestion = this.getCurrentQuestion();
  if (!currentQuestion) return false;

  const questionState = this.formState[currentQuestion.id];
  if (!questionState) return false;

  const isOpenQuestion = currentQuestion.type === FieldType.OPEN;
  
  if (isOpenQuestion) {
    const openAnswer = questionState.openAnswer?.trim() || '';
    return openAnswer.length >= 10;
  }

  const hasSelectedAnswers = questionState.answers.length > 0;
  
  if (!hasSelectedAnswers) {
    return false;
  }

  const selectedAnswers = currentQuestion.config?.surveyQuestionConfig?.answers?.filter(
    answer => questionState.answers.includes(answer.id)
  ) || [];
  
  const hasTextAndOpenAnswer = selectedAnswers.some(
    answer => answer.type === FieldType.TEXT_AND_OPEN_ANSWER
  );
  
  if (hasTextAndOpenAnswer) {
    const openAnswer = questionState.openAnswer?.trim() || '';
    return openAnswer.length >= 10;
  }

  return true;
}


  handleAnswerChange(questionId: string, answerId: string, checked: boolean) {
  const currentQuestion = this.getCurrentQuestion();
  if (!currentQuestion) return;

  const isSingleSelect = currentQuestion.type === FieldType.TEXT_SINGLE ||
                        currentQuestion.type === FieldType.IMAGE_SINGLE;

  const newFormState = { ...this.formState };
  
  const selectedAnswer = currentQuestion.config?.surveyQuestionConfig?.answers?.find(
    answer => answer.id === answerId
  );
  const isNoneOfAbove = selectedAnswer?.config?.surveyAnswerConfig?.text === "NONE OF THE ABOVE";
  
  const noneOfAboveAnswers = currentQuestion.config?.surveyQuestionConfig?.answers?.filter(
    answer => answer.config?.surveyAnswerConfig?.text === "NONE OF THE ABOVE"
  )?.map(answer => answer.id) || [];
  
  if (isSingleSelect) {
    newFormState[questionId] = {
      ...newFormState[questionId],
      answers: checked ? [answerId] : []
    };
  } else {
    const currentAnswers = newFormState[questionId].answers;
    
    if (checked) {
      if (isNoneOfAbove) {
        newFormState[questionId] = {
          ...newFormState[questionId],
          answers: [answerId]
        };
      } else {
        const filteredAnswers = currentAnswers.filter(id => !noneOfAboveAnswers.includes(id));
        newFormState[questionId] = {
          ...newFormState[questionId],
          answers: [...filteredAnswers, answerId]
        };
      }
    } else {
      newFormState[questionId] = {
        ...newFormState[questionId],
        answers: currentAnswers.filter(id => id !== answerId)
      };
    }
  }

  this.formState = newFormState;
}

  handleOpenAnswerChange(questionId: string, value: string) {
    this.formState = {
      ...this.formState,
      [questionId]: {
        ...this.formState[questionId],
        openAnswer: value
      }
    };
  }

  handleNext() {
    if (!this.isCurrentQuestionValid()) return;

    if (this.currentQuestionIndex < this.survey!.questions.length - 1) {
      this.currentQuestionIndex++;
    } else {
      this.submitSurvey();
    }
  }

  async submitSurvey() {
  if (!this.survey) return;

  this.isSubmitting = true;
  this.error = null;

  try {
    const questions: FormValue[] = this.survey.questions.map(question => ({
      id: question.id,
      value: {
        answers: this.formState[question.id].answers.length > 0 ? this.formState[question.id].answers : null,
        openAnswer: this.formState[question.id].openAnswer || null
      }
    }));

    const userSurveyRequest: UserSurveyRequest = {
      questions
    };

    await store.dispatch(
      missionsApi.endpoints.submitSurveyAnswers.initiate({
        jwt: this.jwt,
        locale: this.locale,
        missionCode: this.mission.missionCode,
        answers: userSurveyRequest,
      })
    ).unwrap();

    this.showThankYou = true;
  } catch (error) {
    console.error('Error submitting survey:', error);
    this.error = 'Failed to submit survey. Please try again.';
  } finally {
    this.isSubmitting = false;
  }
}

  renderQuestion(question: FormField) {
    const questionState = this.formState[question.id];
    const questionConfig = question.config?.surveyQuestionConfig;

    if (!questionConfig) return nothing;

    return html`
      <div class="survey-question">
        <h2 class="survey-question__title">${questionConfig.title}</h2>
        
        ${this.renderQuestionSubtitle(question)}
        
        <div class="survey-question__answers">
          ${question.type === FieldType.OPEN
            ? this.renderOpenQuestion(question, questionState)
            : map(questionConfig.answers || [], answer => 
                this.renderAnswer(question, answer, questionState)
              )}
        </div>
      </div>
    `;
  }

  renderQuestionSubtitle(question: FormField) {
  const isMultiple = question.type === FieldType.TEXT_MULTIPLE ||
                    question.type === FieldType.IMAGE_MULTIPLE ||
                    question.type === FieldType.TEXT_MULTIPLE_AND_OPEN;
  
  const isSingle = question.type === FieldType.TEXT_SINGLE ||
                  question.type === FieldType.IMAGE_SINGLE;

  if (isMultiple) {
    return html`<p class="survey-question__subtitle">${this.localize('LXL_SURVEY_SUBTITLE_MULTIPLE')}</p>`;
  } else if (isSingle) {
    return html`<p class="survey-question__subtitle">${this.localize('LXL_SURVEY_SUBTITLE_SINGLE')}</p>`;
  }
    
  return nothing;
}

  renderAnswer(question: FormField, answer: FormField, questionState: any) {
  const answerConfig = answer.config?.surveyAnswerConfig;
  if (!answerConfig) return nothing;

  const isChecked = questionState?.answers?.includes(answer.id) || false;
  const isImage = answer.type === FieldType.IMAGE_ANSWER;
  const isTextAndOpen = answer.type === FieldType.TEXT_AND_OPEN_ANSWER;

  const inputType = question.type === FieldType.TEXT_SINGLE || 
                   question.type === FieldType.IMAGE_SINGLE ? 'radio' : 'checkbox';

  const isNoneOfAbove = isImage && !answerConfig.image;

  return html`
  <div class="survey-answer ${isImage ? 'survey-answer--image' : 'survey-answer--text'}">
    <label class="survey-answer__label">
      <div class="survey-answer__main-content">
        <input
          type="${inputType}"
          name="question-${question.id}"
          value="${answer.id}"
          .checked=${isChecked}
          @change=${(e: Event) => {
            const target = e.target as HTMLInputElement;
            this.handleAnswerChange(question.id, answer.id, target.checked);
          }}
        />
        
        ${isImage 
          ? html`
              <div class="survey-answer__image-container ${isNoneOfAbove ? 'survey-answer__image-container--no-image' : ''}">
                ${!isNoneOfAbove
                  ? html`
                      <img src="${answerConfig.image}" alt="${answerConfig.text}" class="survey-answer__image" />
                      <span class="survey-answer__text">${answerConfig.text}</span>
                    `
                  : html`
                      <span class="survey-answer__text">${answerConfig.text}</span>
                    `
                }
              </div>
            `
          : html`<span class="survey-answer__text">${answerConfig.text}</span>`
        }
      </div>
    </label>
    
    ${isTextAndOpen && isChecked
      ? html`
          <textarea
            class="survey-answer__open-input"
            placeholder="Write your answer here (at least 10 words)"
            .value=${questionState?.openAnswer || ''}
            @input=${(e: Event) => {
              const target = e.target as HTMLTextAreaElement;
              this.handleOpenAnswerChange(question.id, target.value);
            }}
            maxlength="${answer.constraint?.maxLength || 2000}"
          ></textarea>
        `
      : nothing
    }
  </div>
`;
}

  renderOpenQuestion(question: FormField, questionState: any) {
  return html`
    <textarea
      class="survey-open-question"
      placeholder="${this.localize('LXL_SURVEY_OPEN_QUESTION_PLACEHOLDER')}"
      .value=${questionState?.openAnswer || ''}
      @input=${(e: Event) => {
        const target = e.target as HTMLTextAreaElement;
        this.handleOpenAnswerChange(question.id, target.value);
      }}
      maxlength="${question.constraint?.maxLength || 2000}"
    ></textarea>
  `;
}

  renderThankYou() {
    return html`
      <div class="survey-thank-you">
        <h2>${this.localize('LXL_SURVEY_THANK_YOU_TITLE')}</h2>
        <p>
          ${this.localize('LXL_SURVEY_THANK_YOU_DESCRIPTION', [this.mission.points])}
        </p>
        <lxl-component-button 
          @click=${() => {
          if (this.onSurveyCompleted) {
            this.onSurveyCompleted();
          }
          this.modalElement.close();
        }}
          class="survey-thank-you__button"
        >
          ${this.localize('LXL_SURVEY_BACK_TO_PROGRAM')}
        </lxl-component-button>
      </div>
    `;
  }

  render() {
    if (this.isLoading) {
      return html`
        <lxl-component-modal prevent-backdrop-close>
          <div class="survey-modal survey-modal--loading">
            <div class="survey-loading">${this.localize('LXL_SURVEY_LOADING')}</div>
          </div>
        </lxl-component-modal>
      `;
    }

    if (this.error) {
      return html`
        <lxl-component-modal prevent-backdrop-close>
          <div class="survey-modal survey-modal--error">
            <div class="survey-error">
              <h3>${this.localize('LXL_SURVEY_ERROR_TITLE')}</h3>
              <p>${this.localize('LXL_SURVEY_ERROR_MESSAGE')}</p>
              <lxl-component-button @click=${() => this.modalElement.close()}>
                ${this.localize('LXL_SURVEY_CLOSE')}
              </lxl-component-button>
            </div>
          </div>
        </lxl-component-modal>
      `;
    }

    if (this.showThankYou) {
      return html`
        <lxl-component-modal prevent-backdrop-close>
          <div class="survey-modal survey-modal--thank-you">
            ${this.renderThankYou()}
          </div>
        </lxl-component-modal>
      `;
    }

    if (!this.survey) {
      return nothing;
    }

    const currentQuestion = this.getCurrentQuestion();
    if (!currentQuestion) {
      return nothing;
    }

    const progress = ((this.currentQuestionIndex + 1) / this.survey.questions.length) * 100;
    const isLastQuestion = this.currentQuestionIndex === this.survey.questions.length - 1;

    return html`
      <lxl-component-modal prevent-backdrop-close>
        <div class="survey-modal">
          <div class="survey-header">
            <h1 class="survey-title">${this.survey.title}</h1>
            <div class="survey-progress">
              <div class="survey-progress__bar" style="width: ${progress}%"></div>
            </div>
          </div>
          
          <div class="survey-content">
            ${this.renderQuestion(currentQuestion)}
          </div>
          
          <div class="survey-footer">
            <lxl-component-button 
                @click=${this.handleNext}
                ?disabled=${!this.isCurrentQuestionValid() || this.isSubmitting}
                class="survey-next-button"
            >
            ${this.isSubmitting 
              ? this.localize('LXL_SURVEY_SUBMITTING') 
              : isLastQuestion 
              ? this.localize('LXL_SURVEY_SUBMIT') 
              : this.localize('LXL_SURVEY_NEXT')}
            </lxl-component-button>
          </div>
        </div>
      </lxl-component-modal>
    `;
  }
}

declare global {
  interface HTMLElementTagNameMap {
    'lxl-component-survey-modal': LxlComponentSurveyModal;
  }
}
